import React, { useState } from 'react';
import { Rocket } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import JSZip from 'jszip';
import { generateSeoPage } from '../services/SeoPageGenerator';
import { generateCityPage } from '../services/CityPageGenerator';
import { buildIndexHtml, findCountryPages, generateCountryPage } from '../services/HubPageGenerator';
import {
  SITE_BASE, getCountryName, createSlug, cityKeyOf, findCityPages,
  buildRegistry, buildSitemapXml, forwardHtml,
} from '../services/guidesSite';
import { CITY_CONTENT } from '../../data/cityPages';

/**
 * Single button that generates the complete /guides/ folder:
 *   - All individual SEO pages organized by country
 *   - City pages (/guides/<country>/<city>/<city>.html, index.html forwarding there) for cities with enough guides
 *   - guides-registry.js
 *   - index.html (listing all guides and city pages)
 *   - sitemap.xml
 *   - Shared assets (seo-styles.css, seo-scripts.js, seo-header.html, seo-footer.html, city-page.css, city-page.js, guides-globe.js)
 *
 * English guides only: translations of the same tours are left out.
 * Pages are built here in the browser. The old per-guide cloud function (backendItineraryBuilder_generateSeoZip)
 * allows only 2 packages per hour, so it cannot build 300+ guides.
 *
 * First-stop audio: the recordings in Firebase Storage are private, so they are downloaded through the
 * fetchGuideMedia function (functions/index.js) and saved as audio-1.mp3 next to each page. At the start you
 * choose to download every recording again, or to keep the audio-1.mp3 copies already on the website.
 * Upload the ZIP on top of the existing /guides/ folder, without deleting the guide folders.
 */

const LIVE_SITE = 'https://www.guidesofia.com';
const PARALLEL = 6;

const firstStopCoords = (point) => {
  const c = (point && point.coordinates) || {};
  const lat = c.latitude !== undefined ? c.latitude : c.lat;
  const lng = c.longitude !== undefined ? c.longitude : c.lng;
  return { lat, lng };
};

// Does this audio file play for a visitor? (A media element needs no CORS, unlike fetch.)
const canPlay = (url, timeoutMs = 12000) => new Promise(resolve => {
  if (!url) return resolve(false);
  const audio = new Audio();
  let done = false;
  const finish = (ok) => {
    if (done) return;
    done = true;
    clearTimeout(timer);
    audio.removeAttribute('src');
    audio.load();
    resolve(ok);
  };
  const timer = setTimeout(() => finish(false), timeoutMs);
  audio.preload = 'metadata';
  audio.addEventListener('loadedmetadata', () => finish(true));
  audio.addEventListener('error', () => finish(false));
  audio.src = url;
});

// 'en', 'en-US', 'English'…
const isEnglish = (language) => /^en([-_].*)?$/i.test(String(language).trim()) || /^english$/i.test(String(language).trim());

// Runs fn over items, `limit` at a time
const runLimited = async (items, limit, fn) => {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const i = next++;
      await fn(items[i], i);
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
};

export const GenerateAllSeoButton = () => {
  const { currentUser } = useAuth();
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState({ current: 0, total: 0, step: '' });

  const API_BASE_URL = 'https://us-central1-guidesofia-access-control.cloudfunctions.net';

  // Shared assets to fetch from /public and include in ZIP root
  const SHARED_ASSETS = [
    'seo-styles.css',
    'seo-scripts.js',
    'seo-header.html',
    'seo-footer.html',
    'city-page.css',
    'city-page.js',
    'guides-globe.js',
  ];

  // ─── Main handler ────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!currentUser) {
      toast.error('You must be logged in');
      return;
    }

    const confirmed = window.confirm(
      'This will generate the complete /guides/ folder for all published guides. This may take several minutes. Continue?'
    );
    if (!confirmed) return;

    // Audio already on the website can be kept (small ZIP) or downloaded again (about 2 MB per guide)
    const redownloadAudio = window.confirm(
      'Download ALL first-stop audio again?\n\n' +
      'OK = download every recording (large ZIP, about 2 MB per guide; use this if recordings changed).\n' +
      'Cancel = keep the audio already on the website and download only what is missing.'
    );

    setGenerating(true);
    const masterZip = new JSZip();

    // One token for the whole run; renewed only if the server rejects it
    let token = await currentUser.getIdToken();
    const fetchGuide = async (url) => {
      const call = () => fetch(`${API_BASE_URL}/fetchGuide?url=${encodeURIComponent(url)}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      let res = await call();
      if (res.status === 401) {
        token = await currentUser.getIdToken(true);
        res = await call();
      }
      return res;
    };

    // One first-stop recording from the private bucket, through the fetchGuideMedia function
    const fetchMedia = async (url) => {
      try {
        const call = () => fetch(`${API_BASE_URL}/fetchGuideMedia?url=${encodeURIComponent(url)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        let res = await call();
        if (res.status === 401) {
          token = await currentUser.getIdToken(true);
          res = await call();
        }
        if (!res.ok) {
          console.warn(`[Generate All SEO] audio not downloaded (HTTP ${res.status}): ${url}`);
          return null;
        }
        const blob = await res.blob();
        return blob.size > 0 ? blob : null;
      } catch (e) {
        console.warn(`[Generate All SEO] audio download failed: ${url}`, e);
        return null;
      }
    };

    try {
      // ── Step 1: Fetch guide list ──────────────────────────────────────────
      setProgress({ current: 0, total: 0, step: 'Fetching guide list...' });
      toast.loading('Fetching guide list...');

      const listRes = await fetch(`${API_BASE_URL}/getGuides`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      const listData = await listRes.json();

      if (!listData.success || !listData.guides) throw new Error('Failed to fetch guides');

      const allGuides = listData.guides;
      const guidesWithUrls = allGuides.filter(g => g.Name && g.Website);
      console.log(`[Generate All SEO] ${allGuides.length} guides, ${guidesWithUrls.length} with URLs`);

      // ── Step 2: Shared assets ─────────────────────────────────────────────
      setProgress({ current: 0, total: 0, step: 'Fetching shared assets...' });
      toast.dismiss();
      toast.loading('Fetching shared assets...');

      await Promise.all(
        SHARED_ASSETS.map(async (filename) => {
          try {
            const res = await fetch(`/${filename}`);
            if (res.ok) {
              const content = await res.text();
              masterZip.file(filename, content);
              console.log(`[Generate All SEO] ✓ asset: ${filename}`);
            } else {
              console.warn(`[Generate All SEO] asset not found: ${filename}`);
            }
          } catch (e) {
            console.warn(`[Generate All SEO] failed to fetch asset ${filename}:`, e);
          }
        })
      );

      // ── Step 3: Fetch every English guide ─────────────────────────────────
      // Only English guides get a page; translations of the same tours are left out.
      toast.dismiss();
      const candidates = guidesWithUrls.filter(g => !g.Language || isEnglish(g.Language));
      const total = candidates.length;
      let finished = 0;
      const failed = [];
      const loaded = [];   // { meta, fullGuide }
      setProgress({ current: 0, total, step: 'Fetching guides...' });

      await runLimited(candidates, PARALLEL, async (meta) => {
        const guideName = meta.Name?.trim() || 'Untitled';
        try {
          const fullRes = await fetchGuide(meta.Website);
          if (!fullRes.ok) throw new Error(`fetchGuide HTTP ${fullRes.status}`);
          const fullGuide = await fullRes.json();
          // the list may not say the language: then the guide itself decides
          if (!meta.Language && !isEnglish(fullGuide.metadata?.language || 'en')) return;
          loaded.push({ meta, fullGuide });
        } catch (err) {
          console.error(`[Generate All SEO] Error on ${guideName}:`, err);
          failed.push(guideName);
        } finally {
          finished++;
          setProgress({ current: finished, total, step: guideName });
          if (finished % 10 === 0 || finished === total) {
            toast.dismiss();
            toast.loading(`(${finished}/${total}) fetching guides`);
          }
        }
      });

      // ── Step 4: One folder per guide ──────────────────────────────────────
      // Two guides with the same name used to share one folder, and one page silently replaced the other.
      // The oldest guide (lowest ID) keeps the plain address; the others get the ID added.
      loaded.sort((a, b) => Number(a.meta.ID || 0) - Number(b.meta.ID || 0));
      const takenPaths = new Set();
      loaded.forEach(item => {
        const { meta, fullGuide } = item;
        const name = fullGuide.metadata?.name || meta.Name.trim();
        item.countrySlug = createSlug(getCountryName(fullGuide.metadata?.country || meta.Country || ''));
        let slug = createSlug(name);
        if (takenPaths.has(`${item.countrySlug}/${slug}`)) {
          slug = `${slug}-${meta.ID || takenPaths.size}`;
          console.warn(`[Generate All SEO] "${name}" (ID ${meta.ID}) shares its name with another guide: published as ${slug}`);
        }
        takenPaths.add(`${item.countrySlug}/${slug}`);
        item.slug = slug;
        item.pageUrl = `/guides/${item.countrySlug}/${slug}/${slug}-seo.html`;
      });

      // ── Step 5: Pages and first-stop audio ───────────────────────────────
      // Audio comes from Firebase Storage through fetchGuideMedia and is saved as audio-1.mp3 next to the page.
      // Fallbacks: the copy already on the website, then the original if it still plays; otherwise no player.
      // Every page links its city page and up to six other guides of the same city, so the city list comes first.
      const metaOf = (item) => ({ ...item.meta, pageUrl: item.pageUrl });
      const cityPagesAhead = findCityPages(loaded.map(metaOf));
      const sameCity = {};
      loaded.forEach(item => { (sameCity[cityKeyOf(item.meta)] = sameCity[cityKeyOf(item.meta)] || []).push(item); });
      const audio = { downloaded: 0, website: 0, original: 0, none: 0 };
      const byPage = [];
      finished = 0;
      setProgress({ current: 0, total: loaded.length, step: 'Building pages and audio...' });

      await runLimited(loaded, PARALLEL, async (item) => {
        const { meta, fullGuide, countrySlug, slug, pageUrl } = item;
        const folder = `${countrySlug}/${slug}`;
        const points = fullGuide.points || [];
        const original = points[0]?.audioUrl;
        let sample = null;

        if (original) {
          const liveCopy = `${LIVE_SITE}/guides/${folder}/audio-1.mp3`;
          const keepExisting = !redownloadAudio && await canPlay(liveCopy);
          const blob = keepExisting ? null : await fetchMedia(original);
          if (keepExisting) {
            sample = `/guides/${folder}/audio-1.mp3`; audio.website++;
          } else if (blob) {
            masterZip.file(`${folder}/audio-1.mp3`, blob, { compression: 'STORE' });
            sample = `/guides/${folder}/audio-1.mp3`; audio.downloaded++;
          } else if (await canPlay(liveCopy)) {
            sample = `/guides/${folder}/audio-1.mp3`; audio.website++;
          } else if (await canPlay(original)) {
            sample = original; audio.original++;
          } else {
            audio.none++;
          }
        }

        const cityKey = cityKeyOf(meta);
        const cityPage = cityPagesAhead.find(c => c.key === cityKey);
        const ownName = fullGuide.metadata?.name || meta.Name.trim();
        const seenNames = new Set([ownName]);
        const related = (sameCity[cityKey] || [])
          .filter(o => {
            const n = o.fullGuide.metadata?.name || o.meta.Name.trim();
            if (o === item || seenNames.has(n)) return false;
            seenNames.add(n);
            return true;
          })
          .slice(0, 6)
          .map(o => ({
            name: o.fullGuide.metadata?.name || o.meta.Name.trim(),
            url: o.pageUrl,
            thumbnail: o.meta.Photo || o.fullGuide.metadata?.thumbnailUrl || o.fullGuide.points?.[0]?.photoUrl || '',
            stops: (o.fullGuide.points || []).length,
          }));
        const html = generateSeoPage(fullGuide, {
          canonicalUrl: SITE_BASE + pageUrl,
          audioSrc: sample === original ? original : sample && './audio-1.mp3',
          country: getCountryName(fullGuide.metadata?.country || meta.Country || ''),
          countrySlug,
          cityPage: cityPage ? { city: cityPage.city, url: cityPage.url } : null,
          related,
        });
        masterZip.file(`${folder}/${slug}-seo.html`, html);

        byPage.push({
          meta: { ...meta, pageUrl },
          entry: {
            meta,
            slug,
            name: fullGuide.metadata?.name || meta.Name.trim(),
            url: pageUrl,
            language: fullGuide.metadata?.language || meta.Language || 'en',
            stops: points.length,
            firstStopName: points[0]?.name || '',
            firstStop: firstStopCoords(points[0]),
            // every stop's position: the city page marks the places our guide covers
            points: points.map(p => ({ name: p.name, ...firstStopCoords(p) })).filter(p => p.lat !== undefined && p.lat !== null && p.lat !== ''),
            thumbnail: meta.Photo || fullGuide.metadata?.thumbnailUrl || points[0]?.photoUrl || '',
            description: meta.Description || fullGuide.metadata?.description || '',
            sample,
          },
        });
        finished++;
        setProgress({ current: finished, total: loaded.length, step: slug });
        if (finished % 10 === 0 || finished === loaded.length) {
          toast.dismiss();
          toast.loading(`(${finished}/${loaded.length}) pages and audio`);
        }
      });

      byPage.sort((a, b) => a.meta.Name.localeCompare(b.meta.Name));
      const generated = byPage.map(v => v.meta);
      const cityEntries = byPage.map(v => v.entry);
      console.log(`[Generate All SEO] ${generated.length} English guide pages (${guidesWithUrls.length - candidates.length} non-English guides skipped). ` +
        `First-stop audio: ${audio.downloaded} downloaded, ${audio.website} already on the website, ${audio.original} original link, ${audio.none} without audio.`);

      // ── Step 6: City pages ────────────────────────────────────────────────
      setProgress({ current: total, total, step: 'Building city pages...' });
      toast.dismiss();
      toast.loading('Building city pages...');

      // Only guides whose page was generated are listed anywhere, so no link points to a missing page.
      // The planner's research (public/city-research, from guide-studio/city_pages.py) adds the planning
      // sections, and a page for every researched city that passes its bar, with or without guides.
      let research = {};
      try {
        const res = await fetch('/city-research/index.json');
        if (res.ok) research = await res.json();
      } catch (e) {
        console.warn('[Generate All SEO] no city research found, city pages without planning sections:', e);
      }
      const cityPages = findCityPages(generated, research);
      const countryPages = findCountryPages(generated, cityPages);
      const countrySlugs = new Set(countryPages.map(k => k.countrySlug));
      for (const city of cityPages) {
        const entries = cityEntries.filter(c => cityKeyOf(c.meta) === city.key);
        const content = CITY_CONTENT[city.key] || {};
        const folder = `${city.countrySlug}/${city.citySlug}`;
        let cityResearch = null;
        if (city.research) {
          try {
            const res = await fetch(`/city-research/${folder}.json`);
            if (res.ok) cityResearch = await res.json();
          } catch (e) {
            console.warn(`[Generate All SEO] research for ${folder} not loaded:`, e);
          }
        }
        const page = { ...city, countryUrl: countrySlugs.has(city.countrySlug) ? `/guides/${city.countrySlug}/` : null };
        const { html, stats } = generateCityPage(page, entries, content, cityPages, cityResearch);
        masterZip.file(`${folder}/${city.citySlug}.html`, html);
        // the research's place photos and map go to img/places/ beside the page
        if (cityResearch) {
          const files = [...new Set([...(cityResearch.places || []).filter(p => p.photo).map(p => p.photo.file),
            ...(cityResearch.map ? [cityResearch.map.file] : [])])];
          await Promise.all(files.map(async (f) => {
            try {
              const res = await fetch(`/city-research/${folder}/${f}`);
              if (res.ok) masterZip.file(`${folder}/img/places/${f}`, await res.blob());
            } catch (e) {
              console.warn(`[Generate All SEO] city research file ${folder}/${f} not found:`, e);
            }
          }));
        }
        masterZip.file(`${folder}/index.html`, forwardHtml(city.url, `${city.city} audio guides`));

        await Promise.all((content.images || []).map(async (img) => {
          try {
            const res = await fetch(`/city-pages/${city.citySlug}/${img}`);
            if (res.ok) masterZip.file(`${folder}/img/${img}`, await res.blob());
            else console.warn(`[Generate All SEO] city image not found: public/city-pages/${city.citySlug}/${img}`);
          } catch (e) {
            console.warn(`[Generate All SEO] failed to fetch city image ${img}:`, e);
          }
        }));
        console.log(`[Generate All SEO] ✓ city page ${folder}/ (${stats.guides} guides, ${stats.pages} pages, ${stats.samples} samples)`);
      }

      // ── Step 7: index.html, country pages, sitemap.xml, guides-registry.js ──
      toast.dismiss();
      toast.loading('Building index, country pages, sitemap, and registry...');

      countryPages.forEach(k => masterZip.file(`${k.countrySlug}/index.html`, generateCountryPage(k, generated)));
      // the world's best-known sights for the globe (public/globe/places.json, guide-studio/globe_places.py)
      let sights = [];
      try {
        const res = await fetch('/globe/places.json');
        if (res.ok) sights = (await res.json()).places || [];
      } catch (e) {
        console.warn('No globe sights found, the globe shows our cities and guides only:', e);
      }
      masterZip.file('index.html', buildIndexHtml(generated, cityPages, sights));
      masterZip.file('sitemap.xml', buildSitemapXml(generated, cityPages, countryPages));
      masterZip.file('guides-registry.js', buildRegistry(generated, cityPages));

      // ── Step 8: Download ──────────────────────────────────────────────────
      toast.dismiss();
      toast.loading('Packaging...');

      const zipBlob = await masterZip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 }
      });

      const url = URL.createObjectURL(zipBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `guides-${new Date().toISOString().split('T')[0]}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast.dismiss();
      toast.success(
        `Done! ${generated.length} English guide pages (${audio.downloaded + audio.website + audio.original} with audio) and ${cityPages.length} city pages. ` +
        'Upload on top of the existing /guides/ folder (keep the guide folders: they hold the audio files).',
        { duration: 10000 }
      );
      if (failed.length) {
        console.warn('[Generate All SEO] Not generated (left out of index, sitemap and city pages):', failed);
        toast.error(`${failed.length} guides failed and were left out: ${failed.slice(0, 5).join(', ')}${failed.length > 5 ? '…' : ''}`, { duration: 12000 });
      }

    } catch (err) {
      toast.dismiss();
      console.error('[Generate All SEO] Fatal error:', err);
      toast.error(`Failed: ${err.message}`);
    } finally {
      setGenerating(false);
      setProgress({ current: 0, total: 0, step: '' });
    }
  };

  const progressLabel = progress.total > 0
    ? `${progress.current}/${progress.total} — ${progress.step}`
    : progress.step || 'Generating...';

  return (
    <button
      onClick={handleGenerate}
      disabled={generating}
      className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-gray-400 hover:text-gray-600 border border-gray-200 hover:border-gray-300 rounded-lg bg-white transition disabled:opacity-40 disabled:cursor-not-allowed"
      title="Generate the complete /guides/ folder and download as ZIP"
    >
      <Rocket size={13} />
      {generating ? (
        <span className="text-sm">{progressLabel}</span>
      ) : (
        'Generate & Download All'
      )}
    </button>
  );
};
